import React, { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ArrowUpRightIcon } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { CTABand } from "../components/CTABand";
import { ParallaxImage } from "../components/ParallaxImage";
import { Reveal } from "../components/Reveal";
import { BLOG_POSTS } from "../data/blog";
import type { BlogPost } from "../data/blog";
import { IMAGES } from "../data/images";
import { EASE } from "../utils/motion";
type Filter = "All" | BlogPost["category"];
const FILTERS: Filter[] = ["All", "Guides", "Admissions", "Campus life"];
function Meta({
  post
}: {
  post: BlogPost;
}) {
  return <div className="flex flex-wrap items-center gap-3 text-xs font-semibold tracking-[0.14em]" data-id="element-658" data-mp="858211xw49jr">
      <span className="text-[#bb1e35]" data-id="element-659" data-mp="3gl1ft90u76x4">{post.category.toUpperCase()}</span>
      <span className="text-[#9aa1a6]" data-id="element-660" data-mp="3boaykidaq5a4">·</span>
      <span className="text-[#6b7378]" data-id="element-661" data-mp="3blf3d1su1upd">{post.readMinutes} MIN READ</span>
      {post.status === "draft" && <span className="border border-[#cfcac0] px-2 py-0.5 text-[10px] text-[#6b7378]" data-id="element-662" data-mp="2239ip6f2o7sf">DRAFT · PENDING APPROVAL</span>}
    </div>;
}
export function Blog() {
  const reduce = useReducedMotion();
  const [filter, setFilter] = useState<Filter>("All");
  const [featured, ...others] = BLOG_POSTS;
  const visible = others.filter(p => filter === "All" || p.category === filter);
  return <>
      <PageHero variant="editorial" eyebrow="THE MCC BLOG" title="Insight for families planning what’s next." description="Guides to the OSSD, admissions and life at Maple." breadcrumb="Blog" data-id="element-663" data-mp="1j8ukvk4hyl66" />

      <main data-id="element-664" data-mp="1s9ankrdf1o9z">
        <section className="mx-auto max-w-[1440px] px-5 pb-24 md:px-9 md:pb-32" data-id="element-665" data-mp="1x69e6zxvfd3f">
          <Link to={`/news/blog/${featured.slug}`} className="group grid gap-10 border-t border-[#061f38] pt-10 lg:grid-cols-12" data-id="element-666" data-mp="1x46fzsmkednm">
            <div className="overflow-hidden lg:col-span-7" data-id="element-667" data-mp="38l66qjt9kkfj">
              <div className="transition-transform duration-700 ease-out group-hover:scale-[1.02]" data-id="element-668" data-mp="38jle4zqifbkb">
                <ParallaxImage src={featured.image} alt={featured.title} className="aspect-[16/10]" priority data-id="element-669" data-mp="18l9m3uef1ujo" />
              </div>
            </div>
            <div className="flex flex-col justify-end lg:col-span-5" data-id="element-670" data-mp="1ihw2o6zplzfw">
              <Meta post={featured} data-id="element-671" data-mp="1di9tc8n053cd" />
              <h2 className="mt-5 text-4xl font-medium leading-[0.98] tracking-[-0.055em] transition-colors duration-200 group-hover:text-[#bb1e35] md:text-5xl" data-id="element-672" data-mp="1djb4se7xjrum">{featured.title}</h2>
              <p className="mt-5 max-w-md text-base leading-7 text-[#4f5963]" data-id="element-673" data-mp="ku471hgav6af">{featured.excerpt}</p>
              <span className="mt-8 inline-flex items-center gap-2 text-sm font-semibold" data-id="element-674" data-mp="zj0ebcaj8f9z">Read the guide <ArrowUpRightIcon size={16} className="transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" data-id="element-675" data-mp="us1sjx6ryozc" /></span>
            </div>
          </Link>
        </section>

        <section className="border-t border-[#dedbd3] bg-[#f2f0eb] py-20 md:py-28" data-id="element-676" data-mp="23mhtmuoestt">
          <div className="mx-auto max-w-[1440px] px-5 md:px-9" data-id="element-677" data-mp="2dqg9p8u9rcev">
            <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end" data-id="element-678" data-mp="2dp53z3h9jul4">
              <h2 className="text-4xl font-medium tracking-[-0.06em] md:text-5xl" data-id="element-679" data-mp="2iir7a51jem49">Latest</h2>
              <div role="tablist" aria-label="Filter posts" className="flex flex-wrap gap-1" data-id="element-680" data-mp="24rconzbjkmxq">
                {FILTERS.map(f => <button key={f} role="tab" aria-selected={filter === f} onClick={() => setFilter(f)} className={`relative px-4 py-2 text-sm transition-colors duration-200 ${filter === f ? "text-white" : "text-[#4f5963] hover:text-[#061f38]"}`} data-id="element-681" data-mp="28p0kd4l26jqo">
                    {filter === f && <motion.span layoutId="blog-filter" transition={{
                  duration: reduce ? 0 : 0.35,
                  ease: EASE
                }} className="absolute inset-0 bg-[#061f38]" data-id="element-682" data-mp="2nfrdqh2atqvw" />}
                    <span className="relative" data-id="element-683" data-mp="1c0kwre1iekg7">{f}</span>
                  </button>)}
              </div>
            </div>

            <AnimatePresence mode="wait" data-id="element-684" data-mp="1h1s4jd3njjmv">
              <motion.div key={filter} initial={{
              opacity: 0,
              y: reduce ? 0 : 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0
            }} transition={{
              duration: reduce ? 0.01 : 0.3,
              ease: EASE
            }} className="mt-10 grid gap-x-8 gap-y-14 border-t border-[#061f38] pt-10 md:grid-cols-2" data-id="element-685" data-mp="1w9u7fdj3qpcl">
                {visible.length > 0 ? visible.map((p, i) => <Reveal key={p.slug} delay={i * 0.06} data-id="element-686" data-mp="1ijqoavfirbrd" data-mpi={i}>
                    <Link to={`/news/blog/${p.slug}`} className="group block" data-id="element-687" data-mp="2f8g8wtij6ih1">
                      <div className="overflow-hidden" data-id="element-688" data-mp="2f6c4magpzhg8"><img src={p.image} alt="" loading="lazy" className="aspect-[3/2] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" data-id="element-689" data-mp="2a93osgj1npi5" /></div>
                      <div className="mt-6" data-id="element-690" data-mp="218x9qq7p6hgv"><Meta post={p} data-id="element-691" data-mp="219qa99g2caqk" /></div>
                      <h3 className="mt-3 text-2xl font-medium leading-tight tracking-[-0.04em] transition-colors duration-200 group-hover:text-[#bb1e35] md:text-3xl" data-id="element-692" data-mp="218nmvbhy8qfe">{p.title}</h3>
                      <p className="mt-3 max-w-lg text-sm leading-6 text-[#4f5963]" data-id="element-693" data-mp="psyu23i0kx6o">{p.excerpt}</p>
                    </Link>
                  </Reveal>) : <p className="py-10 text-lg text-[#4f5963] md:col-span-2" data-id="element-694" data-mp="36ff3gb4gxj6c">No {filter.toLowerCase()} posts yet. New articles appear here as MCC publishes them.</p>}
              </motion.div>
            </AnimatePresence>
          </div>
        </section>
      </main>

      <CTABand title="Questions a guide can’t answer? Ask us directly." image={IMAGES.campus} data-id="element-695" data-mp="3k7v79e571l8b" />
    </>;
}